export type ShopifyNavigationClick = Pick<Event, 'defaultPrevented' | 'preventDefault' | 'isTrusted'> &
  Partial<Pick<MouseEvent, 'button' | 'metaKey' | 'ctrlKey' | 'shiftKey' | 'altKey'>>
type NavigationClick = MouseEvent

/** App Bridge can replay parent navigation through element.click(); callers must not treat it as fresh user input. */
export function isProgrammaticShopifyNavigation(event: ShopifyNavigationClick) {
  return event.isTrusted === false
}

function getAnchor(target: EventTarget | null): HTMLAnchorElement | null {
  if (!(target instanceof Element)) return null
  return target.closest('a')
}

function getAppTargetUrl(anchor: HTMLAnchorElement) {
  return new URL(anchor.href, window.location.href)
}

export function isEligibleShopifyAppNavigationClick(event: NavigationClick, expectedShop?: string | null) {
  if (
    event.defaultPrevented ||
    event.button !== 0 ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey
  ) {
    return false
  }

  const anchor = getAnchor(event.target)
  if (!anchor || anchor.hasAttribute('download')) return false

  const target = anchor.getAttribute('target')
  if (target && target !== '_self') return false

  const url = getAppTargetUrl(anchor)
  if (!url || (url.protocol !== 'http:' && url.protocol !== 'https:') || url.origin !== window.location.origin) {
    return false
  }

  const targetShop = url.searchParams.get('shop')
  if (expectedShop && targetShop && targetShop !== expectedShop) return false

  return true
}

export async function handleShopifyAppNavigationClick(
  event: NavigationClick,
  expectedShop?: string | null,
  navigate?: (commit: () => void) => Promise<void | boolean>,
) {
  if (!isEligibleShopifyAppNavigationClick(event, expectedShop)) return false

  const anchor = getAnchor(event.target)
  if (!anchor) return false
  return handleShopifyAppLinkClick(event, anchor.href, expectedShop, navigate)
}

/** Explicit target for React links and native web-component buttons, including shadow DOM controls. */
export async function handleShopifyAppLinkClick(
  event: ShopifyNavigationClick,
  href: string,
  expectedShop?: string | null,
  navigate?: (commit: () => void) => Promise<void | boolean>,
  state: unknown = null,
) {
  if (
    event.defaultPrevented ||
    (event.button !== undefined && event.button !== 0) ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey
  )
    return false
  const url = new URL(href, window.location.href)
  if (!['http:', 'https:'].includes(url.protocol) || url.origin !== window.location.origin) return false
  const targetShop = url.searchParams.get('shop')
  if (expectedShop && targetShop && targetShop !== expectedShop) return false
  // Cancel synchronously so App Bridge observes an app-handled link; modified clicks retain native behavior.
  event.preventDefault()
  const nextUrl = `${url.pathname}${url.search}${url.hash}`
  const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`
  if (nextUrl !== currentUrl) {
    const commit = () => {
      window.history.pushState(state, '', nextUrl)
      window.dispatchEvent(new PopStateEvent('popstate', { state }))
    }
    if (navigate) {
      if ((await navigate(commit)) === false) return false
    } else commit()
  }
  return true
}
